/**
 * The authoritative form patch of a "Browse Azure" pick (Option C, C0 / C3.1: routed through
 * `useEngineDrafts.applyPatch(engine, patch, 'azure_pick')`, which switches to the resource's engine first).
 *
 * Server always; database always — the resource's, or empty when it has none, so a stale database of the draft it lands
 * on never names a database on the picked server; port, SSL, Encrypt / Trust and authentication where the resource's
 * draft defines them (Azure SQL and Managed Instance picks carry Encrypt on and Trust off); the ARM id of every kind with
 * origin `'picker'`; a storage account also brings its data-plane endpoints and services, is always account scope and
 * never the emulator (`draft.hns` informs the pick only — the account-info probe decides HNS at connect time), and it
 * clears the form's "Connect using" pick. The Name is suggested from the resource name only while it is empty. A pick
 * never carries a password, and never connects.
 */
import type { AzureDiscoveredResource } from '../../../../types/azureDiscovery';
import type { EnginePatch } from './useEngineDrafts';

export function azureResourceToPatch(resource: AzureDiscoveredResource, currentAlias: string | undefined): EnginePatch {
  const draft = resource.connectionDraft;
  const patch: EnginePatch = { server: draft.server };
  if (draft.port !== undefined) patch.port = draft.port;
  patch.database = draft.database ?? '';
  if (draft.ssl !== undefined) patch.ssl = draft.ssl;
  if (draft.encrypt !== undefined) patch.encrypt = draft.encrypt;
  if (draft.trustServerCertificate !== undefined) patch.trustServerCertificate = draft.trustServerCertificate;
  if (draft.authentication !== undefined) patch.authentication = draft.authentication as EnginePatch['authentication'];
  if (draft.azureStorageEndpoints !== undefined) patch.azureStorageEndpoints = draft.azureStorageEndpoints;
  if (draft.azureStorageServices !== undefined) patch.azureStorageServices = draft.azureStorageServices;
  if (resource.engine === 'azurestorage') {
    patch.azureStorageScope = { kind: 'account' };
    patch.azureStorageEmulator = false;
    // The picked account decides "Connect using"; an earlier "Single resource" pick steps aside (C3).
    patch.azureStorageModePick = undefined;
  }
  if (draft.armResourceId !== undefined) {
    patch.azureResourceId = draft.armResourceId;
    patch.azureResourceIdOrigin = 'picker';
  }
  if (!String(currentAlias ?? '').trim()) patch.alias = resource.name;
  return patch;
}
